<script lang="ts" setup>
import { useRoute, useRouter } from 'vue-router';
import { flows } from '@/parcours/flows';
import DynamicForm from '@/organisms/DynamicForm.vue';
import { useFlow } from '@/parcours/composables/useFlow';
import { flowStore, restoreFlow, setFlowStep } from '@/parcours/stores/flowStore';
import { IonContent, IonFooter, IonPage, onIonViewWillEnter } from '@ionic/vue';
import SkeletonContent from '@/molecules/SkeletonContent.vue';
import { computed, ref, watch } from 'vue';
import { handleBackNavigation } from '@/helpers/navigation.ts';
import { LINKS } from '@/utils/links';
import TitleHeader from '@/molecules/TitleHeader.vue';
import BaseButton from '@/atoms/BaseButton.vue';
import EndFlowScreen from '@/atoms/EndFlowScreen.vue';
import { Illustrations } from '@/constant/illustrations.ts';
import {
  generateAlertText,
  generateBloodText,
  generateSpecialistsAlertText,
} from '@/parcours/flows/shared/helper';
import { useAppModal } from '@/composables/useAppModal';
import BottomButtons from '@/molecules/BottomButtons.vue';
import RowItemContainer from '@/organisms/RowItemContainer.vue';
import { RowItemProps } from '@/molecules/RowItem.vue';
import LocalIcon from '@/atoms/LocalIcon.vue';

const modal = useAppModal();
const route = useRoute();
const router = useRouter();

const flowId = route.meta.flowId as string;
const step = route.meta.step as string;
const initialData = ref(null);
const request = ref(null);

// tentative de restore si store vide
if (!flowStore.flowId) {
  const restored = restoreFlow();

  if (restored && restored.flowId === flowId && restored.step) {
    router.replace({
      name: `flow-${flowId}-${restored.step}`,
      params: route.params,
    });
  }
}


const isLoading = ref(true);

onIonViewWillEnter(async () => {
  // Sync step avec la route courante (fix swipe-back iOS)
  setFlowStep(step);

  try {
    // Certains flows (labo) doivent TOUJOURS relire la DB (cahier Réf 19) : on
    // ignore le cache `initialized` s'ils déclarent `refetchOnEnter`.
    if (flowStore.initialized && !flow.refetchOnEnter) {
      initialData.value = flowStore.data;
      request.value = flowStore.data;
      isLoading.value = false;
      return;
    }
    if (flow.onInit) {
      const toto = await flow.onInit({ route });
      request.value = toto;
      initialData.value = toto;
      flowStore.data = { ...flowStore.data, ...toto };
      flowStore.initialized = true;
    }
  } catch (e) {
    console.error(e);
  } finally {
    isLoading.value = false;
  }
});

const emit = defineEmits<{
  (e: 'canSubmit', value: boolean): void;
}>();

const canSubmit = computed(() => {
  return true;
});

const isComponentSubmitAllowed = ref(true);
const componentSubmitAttempted = ref(false);

watch(
  canSubmit,
  (value) => {
    emit('canSubmit', value);
  },
  { immediate: true },
);

const flow = flows[flowId];
if (!flow) throw new Error(`Flow "${flowId}" introuvable`);
const stepConfig = flow.steps[step];

const { next, back, update, submit, isSubmitting } = useFlow(flowId);

const dynamicSchema = computed(() => {
  if (stepConfig.type !== 'form') return stepConfig.schema;

  return {
    ...stepConfig.schema,
    fields: stepConfig.schema.fields.map((field) => {
      const updatedField = { ...field };

      if (initialData.value && initialData.value[field.id] !== undefined) {
        updatedField.initialValue = initialData.value[field.id];
      }

      if (field.id === 'consultation_practitioner') {
        updatedField.highlight = { text: generateAlertText(flowStore.data) };
      }

      if (field.id === 'blood_test') {
        updatedField.highlight = { text: generateBloodText(flowStore.data) };
      }

      if (field.id === 'other_specialists') {
        updatedField.highlight = { text: generateSpecialistsAlertText(flowStore.data) };
      }

      return updatedField;
    }),
  };
});

async function handleSubmit(values) {
  update(values);

  const transition = flow.transitions?.[step];

  if (!transition) return;

  if (typeof transition.sideEffect === 'function') {
    await transition.sideEffect({ data: flowStore.data });
  }

  let result;

  if (typeof transition.next === 'function') {
    result = transition.next({ data: flowStore.data });
  } else {
    result = transition;
  }

  if (result?.customSubmit) {
    if (result.action) await result.action();
    if (result.redirect) router.push(result.redirect);
    return;
  }

  if (result?.submit) {
    await submit(step);
    if (result.action) await result.action();
    if (result.redirect) router.push(result.redirect);
    return;
  }

  next(step);
}

const resolvedProps = computed(() => {
  if (typeof stepConfig.props === 'function') {
    return stepConfig.props({ flowData: flowStore.data, request: request });
  }
  return stepConfig.props || {};
});

type ButtonActionType = 'primary' | 'secondary' | 'destructive' | 'success';

type Action = {
  id?: string;
  label: string | ((data: any) => string);
  onClick: Function;
  variant?: 'button';
  buttonType?: ButtonActionType | ((data: any) => ButtonActionType);
};

const resolveAction = (action: Action) => ({
  label: typeof action.label === 'function' ? action.label(flowStore.data) : action.label,
  buttonType: (typeof action.buttonType === 'function'
    ? action.buttonType(flowStore.data)
    : action.buttonType ?? 'primary') as ButtonActionType,
  onClick: () => action.onClick({ modal, data: flowStore.data, request, router }),
});

const stepActionsItems = computed<RowItemProps[]>(() => {
  const actions = (resolvedProps.value?.actions || []) as Action[];
  return actions
    .filter((a) => !a.variant)
    .map((action) => ({
      title: typeof action.label === 'function' ? action.label(flowStore.data) : action.label,
      id: action.id,
      onClick: () => action.onClick({ modal, data: flowStore.data, request, router }),
    }));
});

const stepButtonActions = computed(() => {
  const actions = (resolvedProps.value?.actions || []) as Action[];
  return actions.filter((a) => a.variant === 'button').map(resolveAction);
});

const showNavButton = computed(
  () =>
    stepConfig.type !== 'form' &&
    !stepConfig.isButtonNextDisabled &&
    stepConfig.type !== 'end',
);
</script>

<template>
  <IonPage
    :class="{
      'page-container': stepConfig.type !== 'end',
      'end-page': stepConfig.type === 'end',
    }"
  >
    <!-- Loader -->
    <SkeletonContent v-if="isLoading" />

    <template v-else>
      <!-- Contenu scrollable avec keyboard avoidance iOS -->
      <IonContent :scroll-y="true" class="flex flex-col">
        <div class="flex flex-col justify-start" :class="{ 'pb-24': showNavButton }">
          <TitleHeader
            v-if="stepConfig.type !== 'end' && stepConfig.header !== 'internal'"
            :description="stepConfig.description"
            :on-back="() => handleBackNavigation()"
            :title="stepConfig.title"
          />

          <DynamicForm
            v-if="stepConfig.type === 'form'"
            :on-submit="handleSubmit"
            :schema="dynamicSchema"
            :submit-button-props="{
              label: stepConfig.submitLabel || 'Suivant',
              secondIconName: 'arrow-right',
            }"
            class="mt-4 space-y-4"
          />

          <component
            :is="stepConfig.component"
            v-else-if="stepConfig.type === 'component'"
            v-model="flowStore.data[stepConfig.id]"
            class="min-h-content mt-4 h-full"
            v-bind="
              typeof stepConfig.props === 'function'
                ? stepConfig.props({ flowData: flowStore.data, request: request })
                : stepConfig.props
            "
            :submit-attempted="componentSubmitAttempted"
            @back="back(step)"
            @next="next(step)"
            @submit="() => submit(step)"
            @can-submit="(val) => { isComponentSubmitAllowed = val; if (val) componentSubmitAttempted = false; }"
          />

          <div v-if="stepActionsItems.length" class="mt-4">
            <RowItemContainer
              :row-items="stepActionsItems"
              class="mb-2 w-full"
              row-item-type="RowItem"
              type="primary"
            >
              <LocalIcon name="chevron-right" />
            </RowItemContainer>
          </div>

          <div v-if="stepButtonActions.length" class="mt-4 space-y-2 px-4">
            <BaseButton
              v-for="(btn, i) in stepButtonActions"
              :key="i"
              :label="btn.label"
              :type="btn.buttonType"
              :on-click="btn.onClick"
              class="w-full"
            />
          </div>

          <EndFlowScreen
            v-if="stepConfig.type === 'end'"
            :description="stepConfig.description"
            :illustration-name="Illustrations.HANDS_DARK"
            :show-close-button="true"
            :title="stepConfig.title || 'Compte rendu envoyé'"
            :redirect-to="LINKS.MISSIONS.SLOT.ID(route.params.missionId, route.params.slotId)"
            button-text="Revenir au créneau"
          />
        </div>
      </IonContent>

      <!-- Bouton de navigation fixé en bas, remonte avec le clavier sur iOS -->
      <IonFooter v-if="showNavButton" class="ion-no-border bg-white">
        <BottomButtons>
          <BaseButton
            :disabled="isSubmitting"
            :label="stepConfig.buttonNextTitle || 'Suivant'"
            :loading="isSubmitting"
            :on-click="() => isComponentSubmitAllowed ? handleSubmit() : (componentSubmitAttempted = true)"
            class="w-full"
            second-icon-name="arrow-right"
          />
        </BottomButtons>
      </IonFooter>
    </template>
  </IonPage>
</template>
